import { createSlice } from "@reduxjs/toolkit";

import { PaymentLedgerType } from "./invoicingAdvance-types";

import {
  addInvoicingAdvanceAction,
  addOtherInvoiceAction,
  allocateBankTransactionToOtherInvoice,
  approveInvoiceAction,
  bulkUploadActionExpenses,
  bulkUploadActionOther,
  bulkUploadActionOtherSuspense,
  bulkUploadActionRevenue,
  cancelEWayBillAction,
  checkDuplicateInvoiceEntryAction,
  deleteInvoiceEntryAction,
  deleteOtherInvoiceEntryAction,
  downloadBulkUploadTemplate,
  downloadInvoicingAdvanceReportsAction,
  downloadOthersReportsAction,
  generateEWayBillAction,
  getAllExpensesInvoicesListAction,
  getAllMappedClientColumnListAction,
  getAllOtherInvoicesListAction,
  getAllPaymentLedgersListAction,
  getBulkUploadHistoryAction,
  getGstrTwoATwoBComparePayloadAction,
  getInvoiceAllCommentAction,
  getInvoiceHistoryDataAction,
  getOthersInvoiceHistoryDataAction,
  getSingleInvoiceEntryAction,
  getSingleOtherInvoiceEntryAction,
  getUploadedBankStatementAction,
  getUploadedBankStatementTransactionCommentList,
  getUploadedBankStatementTransactionList,
  invoiceDownloadDetailsAction,
  invoicingAdvanceLockEntryAction,
  postEwayBillAuthenticationAction,
  postExtendEWayBillAction,
  postInvoiceAllCommentAction,
  postInvoicingAdvanceOCRAction,
  postMultipleBankTransactionAction,
  postTransactionCommentList,
  reviewEwayBillAction,
  sendOverdueEmailApiAction,
  updateInvoicingAdvanceAction,
  updateOtherInvoiceAction,
  updateTransporterEWayBillDetails,
  updateVehicleEWayBillDetails,
  uploadBankStatementAction,
  bankStatementTransactionFileUploadAction,
  getOverdueInvoiceReminderEmailTimelineAction,
  lockUnlockInvoiceAction,
} from "@/redux/invoicingAdvance/actions";
import { initialState } from "@/redux/invoicingAdvance/initialState";
import { invoicingAdvanceReducers } from "@/redux/invoicingAdvance/reducer";

export const invoicingAdvanceSlice = createSlice({
  name: "invoicingAdvance",
  initialState,
  reducers: invoicingAdvanceReducers,
  extraReducers: (builder) => {
    builder.addCase(getAllExpensesInvoicesListAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getAllExpensesInvoicesListAction.fulfilled,
      (state, { payload }) => {
        const { status_code, result, message, success } = payload.data;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          invoicesCount: result?.total_count,
          invoicesList:
            payload.offset > 0
              ? [...(state.invoicesList || []), ...(result?.data ?? [])]
              : result?.data,
          lockedExpensesEntriesDate: result?.lock_entry_date,
          message: message,
        };
      }
    );

    builder.addCase(
      getAllExpensesInvoicesListAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getInvoiceAllCommentAction.pending, (state) => ({
      ...state,
      commentsFetching: true,
    }));

    builder.addCase(
      getInvoiceAllCommentAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message, result } = payload;

        if (success === 0) {
          return {
            ...state,
            commentsFetching: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }

        return {
          ...state,
          commentsFetching: false,
          statusCode: status_code,
          allComments: result,
        };
      }
    );

    builder.addCase(
      getInvoiceAllCommentAction.rejected,
      (state, { error }) => ({
        ...state,
        commentsFetching: false,
        error: error?.message ?? null,
      })
    );
    builder.addCase(postInvoiceAllCommentAction.pending, (state) => ({
      ...state,
      commentsFetching: true,
    }));

    builder.addCase(
      postInvoiceAllCommentAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            commentsFetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          commentsFetching: false,
          statusCode: status_code,
          success: success,
        };
      }
    );

    builder.addCase(
      postInvoiceAllCommentAction.rejected,
      (state, { error }) => ({
        ...state,
        commentsFetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(getInvoiceHistoryDataAction.pending, (state) => ({
      ...state,
      invoiceHistoryFetching: true,
    }));

    builder.addCase(
      getInvoiceHistoryDataAction.fulfilled,
      (state, { payload }) => {
        const { status_code, result, message, success, total_count } =
          payload.data;

        if (success === 0) {
          return {
            ...state,
            invoiceHistoryFetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          invoiceHistoryFetching: false,
          statusCode: status_code,
          invoiceHistoryData:
            payload.offset > 0
              ? [...(state.invoiceHistoryData || []), ...(result || [])]
              : result,
          invoiceHistoryCount: total_count,
        };
      }
    );

    builder.addCase(
      getInvoiceHistoryDataAction.rejected,
      (state, { error }) => ({
        ...state,
        success: 0,
        error: error?.message ?? null,
        invoiceHistoryFetching: false,
      })
    );

    builder.addCase(downloadInvoicingAdvanceReportsAction.pending, (state) => ({
      ...state,
      downloadReportFetching: true,
    }));

    builder.addCase(
      downloadInvoicingAdvanceReportsAction.fulfilled,
      (state) => {
        return {
          ...state,
          downloadReportFetching: false,
        };
      }
    );

    builder.addCase(
      downloadInvoicingAdvanceReportsAction.rejected,
      (state, { error }) => ({
        ...state,
        error: error?.message ?? "Download Failed Try Again",
        downloadReportFetching: false,
      })
    );
    builder.addCase(downloadOthersReportsAction.pending, (state) => ({
      ...state,
      downloadReportFetching: true,
    }));

    builder.addCase(downloadOthersReportsAction.fulfilled, (state) => {
      return {
        ...state,
        downloadReportFetching: false,
      };
    });

    builder.addCase(
      downloadOthersReportsAction.rejected,
      (state, { error }) => ({
        ...state,
        error: error?.message ?? "Download Failed Try Again",
        downloadReportFetching: false,
      })
    );

    builder.addCase(invoicingAdvanceLockEntryAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      invoicingAdvanceLockEntryAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      invoicingAdvanceLockEntryAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(getAllMappedClientColumnListAction.pending, (state) => ({
      ...state,
      columnListFetching: true,
    }));

    builder.addCase(
      getAllMappedClientColumnListAction.fulfilled,
      (state, { payload }) => {
        const { result = [], message, status_code, success } = payload;
        const { expenses, revenues } = result;

        if (success === 0) {
          return {
            ...state,
            columnListFetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          columnListFetching: false,
          statusCode: status_code,
          mappedExpensesColumnsList: expenses,
          mappedRevenueColumnsList: revenues,
          // mappedOthersColumnList: others,
          message,
        };
      }
    );

    builder.addCase(
      getAllMappedClientColumnListAction.rejected,
      (state, { error }) => ({
        ...state,
        error: error?.message ?? null,
        success: 0,
        columnListFetching: false,
      })
    );

    builder.addCase(postInvoicingAdvanceOCRAction.pending, (state) => ({
      ...state,
      OCRFetching: true,
    }));

    builder.addCase(
      postInvoicingAdvanceOCRAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            OCRFetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          OCRFetching: false,
          statusCode: status_code,
          singleInvoiceDetails: result,
        };
      }
    );

    builder.addCase(
      postInvoicingAdvanceOCRAction.rejected,
      (state, { error }) => ({
        ...state,
        OCRFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(addInvoicingAdvanceAction.pending, (state) => ({
      ...state,
      submittingInvoiceForm: true,
    }));

    builder.addCase(
      addInvoicingAdvanceAction.fulfilled,
      (state, { payload }) => {
        const { message, status_code, success } = payload;

        if (success === 0) {
          return {
            ...state,
            submittingInvoiceForm: false,
            success,
            error: message,
            message: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          submittingInvoiceForm: false,
          success,
          statusCode: status_code,
          message: message,
        };
      }
    );

    builder.addCase(addInvoicingAdvanceAction.rejected, (state, { error }) => ({
      ...state,
      submittingInvoiceForm: false,
      success: 0,
      error: error?.message ?? null,
    }));
    builder.addCase(getSingleInvoiceEntryAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getSingleInvoiceEntryAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          singleInvoiceDetails: result,
          message: message,
        };
      }
    );

    builder.addCase(
      getSingleInvoiceEntryAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(approveInvoiceAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(approveInvoiceAction.fulfilled, (state, { payload }) => {
      const { status_code, success, message } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          error: message,
          statusCode: status_code,
          success,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        success,
        message,
      };
    });

    builder.addCase(approveInvoiceAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));

    builder.addCase(invoiceDownloadDetailsAction.pending, (state) => ({
      ...state,
      downloadSingleInvoiceFetching: true,
    }));

    builder.addCase(
      invoiceDownloadDetailsAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message, result } = payload;

        if (success === 0) {
          return {
            ...state,
            downloadSingleInvoiceFetching: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }

        return {
          ...state,
          downloadSingleInvoiceFetching: false,
          statusCode: status_code,
          invoiceDownloadDetails: result,
        };
      }
    );

    builder.addCase(
      invoiceDownloadDetailsAction.rejected,
      (state, { error }) => ({
        ...state,
        downloadSingleInvoiceFetching: false,
        error: error?.message ?? null,
      })
    );
    builder.addCase(deleteInvoiceEntryAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      deleteInvoiceEntryAction.fulfilled,
      (state, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(deleteInvoiceEntryAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));

    builder.addCase(updateInvoicingAdvanceAction.pending, (state) => ({
      ...state,
      submittingInvoiceForm: true,
    }));

    builder.addCase(
      updateInvoicingAdvanceAction.fulfilled,
      (state, { payload }) => {
        const { message, status_code, success } = payload;

        if (success === 0) {
          return {
            ...state,
            submittingInvoiceForm: false,
            success,
            error: message,
            message: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          submittingInvoiceForm: false,
          success,
          statusCode: status_code,
          message: message,
        };
      }
    );

    builder.addCase(
      updateInvoicingAdvanceAction.rejected,
      (state, { error }) => ({
        ...state,
        submittingInvoiceForm: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getBulkUploadHistoryAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getBulkUploadHistoryAction.fulfilled,
      (state, { payload }) => {
        const { status_code, result, message, success, count } = payload.data;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          bulkUploadHistoryData:
            payload.offset > 0
              ? [
                  ...(state.bulkUploadHistoryData || []),
                  ...(result?.data || []),
                ]
              : result,
          bulkUploadCount: count,
        };
      }
    );

    builder.addCase(
      getBulkUploadHistoryAction.rejected,
      (state, { error }) => ({
        ...state,
        success: 0,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(bulkUploadActionExpenses.pending, (state) => ({
      ...state,
      bulkUploadLoading: true,
    }));

    builder.addCase(
      bulkUploadActionExpenses.fulfilled,
      (state, { payload }) => {
        const { success, message, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            bulkUploadLoading: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          bulkUploadLoading: false,
          statusCode: status_code,
          bulkUploadData: payload,
          message,
          error: payload?.error_docs_count > 0 ? message : "",
          success: payload?.error_docs_count > 0 ? 0 : 1,
        };
      }
    );

    builder.addCase(bulkUploadActionExpenses.rejected, (state, { error }) => ({
      ...state,
      bulkUploadLoading: false,
      error: error?.message ?? null,
    }));

    builder.addCase(bulkUploadActionRevenue.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(bulkUploadActionRevenue.fulfilled, (state, { payload }) => {
      const { success, message, status_code } = payload;
      if (success === 0) {
        return {
          ...state,
          fetching: false,
          error: message,
          statusCode: status_code,
          success,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        bulkUploadData: payload,
        error: payload?.error_docs_count > 0 ? message : "",
        success: payload?.error_docs_count > 0 ? 0 : 1,
        message,
      };
    });

    builder.addCase(bulkUploadActionRevenue.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
    }));
    builder.addCase(bulkUploadActionOtherSuspense.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      bulkUploadActionOtherSuspense.fulfilled,
      (state, { payload }) => {
        const { success, message, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          bulkUploadData: payload,
          error: payload?.error_docs_count > 0 ? message : "",
          success: payload?.error_docs_count > 0 ? 0 : 1,
          message,
        };
      }
    );

    builder.addCase(
      bulkUploadActionOtherSuspense.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );
    builder.addCase(bulkUploadActionOther.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(bulkUploadActionOther.fulfilled, (state, { payload }) => {
      const { success, message, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          error: message,
          statusCode: status_code,
          success,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        bulkUploadData: payload,
        error: payload?.error_docs_count > 0 ? message : "",
        success: payload?.error_docs_count > 0 ? 0 : 1,
        message,
      };
    });

    builder.addCase(bulkUploadActionOther.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
    }));

    builder.addCase(reviewEwayBillAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(reviewEwayBillAction.fulfilled, (state, { payload }) => {
      const { status_code, success, message, result } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          error: message,
          success,
        };
      }
      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        eWayBillReviewData: result,
      };
    });

    builder.addCase(reviewEwayBillAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));
    builder.addCase(generateEWayBillAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(generateEWayBillAction.fulfilled, (state, { payload }) => {
      const { status_code, success, message, result } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          error: message,
          success,
          eWayBillError: result?.data?.info
            ? result?.data?.info
            : result?.message,
        };
      }
      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        success,
        message,
      };
    });

    builder.addCase(generateEWayBillAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));

    builder.addCase(cancelEWayBillAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(cancelEWayBillAction.fulfilled, (state, { payload }) => {
      const { status_code, success, message, result } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          error: message,
          statusCode: status_code,
          success,
          eWayBillError: result?.data?.info
            ? result?.data?.info
            : result?.message,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        success,
        message,
      };
    });

    builder.addCase(cancelEWayBillAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));

    builder.addCase(postExtendEWayBillAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      postExtendEWayBillAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            success,
            eWayBillError: result?.data?.info
              ? result?.data?.info
              : result?.message,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(postExtendEWayBillAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));

    builder.addCase(postEwayBillAuthenticationAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      postEwayBillAuthenticationAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      postEwayBillAuthenticationAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(updateVehicleEWayBillDetails.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      updateVehicleEWayBillDetails.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            success,
            eWayBillUpdateError: result?.data?.info
              ? result?.data?.info
              : result?.message,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      updateVehicleEWayBillDetails.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );
    builder.addCase(updateTransporterEWayBillDetails.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      updateTransporterEWayBillDetails.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            success,
            eWayBillUpdateError: result?.data?.info
              ? result?.data?.info
              : result?.message,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      updateTransporterEWayBillDetails.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );
    builder.addCase(checkDuplicateInvoiceEntryAction.pending, (state) => ({
      ...state,
      submittingInvoiceForm: true,
    }));

    builder.addCase(
      checkDuplicateInvoiceEntryAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message, is_duplicate_invoice } = payload;

        if (success === 0) {
          return {
            ...state,
            submittingInvoiceForm: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }

        return {
          ...state,
          submittingInvoiceForm: false,
          statusCode: status_code,
          isDuplicateInvoiceEntry: is_duplicate_invoice,
        };
      }
    );

    builder.addCase(
      checkDuplicateInvoiceEntryAction.rejected,
      (state, { error }) => ({
        ...state,
        submittingInvoiceForm: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(downloadBulkUploadTemplate.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      downloadBulkUploadTemplate.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          message,
          success,
        };
      }
    );

    builder.addCase(
      downloadBulkUploadTemplate.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? "Download Failed Try Again",
      })
    );
    builder.addCase(getAllOtherInvoicesListAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getAllOtherInvoicesListAction.fulfilled,
      (state, { payload }) => {
        const { status_code, result, message, success } = payload.data;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          invoicesCount: result?.total_count,
          invoicesList:
            payload.offset > 0
              ? [...(state.invoicesList || []), ...(result?.data || [])]
              : result?.data,
          lockedExpensesEntriesDate: result?.lock_entry_date,
          message: message,
        };
      }
    );

    builder.addCase(
      getAllOtherInvoicesListAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(deleteOtherInvoiceEntryAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      deleteOtherInvoiceEntryAction.fulfilled,
      (state, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      deleteOtherInvoiceEntryAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );
    builder.addCase(getOthersInvoiceHistoryDataAction.pending, (state) => ({
      ...state,
      invoiceHistoryFetching: true,
    }));

    builder.addCase(
      getOthersInvoiceHistoryDataAction.fulfilled,
      (state, { payload }) => {
        const { status_code, result, message, success, total_count } =
          payload.data;

        if (success === 0) {
          return {
            ...state,
            invoiceHistoryFetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          invoiceHistoryFetching: false,
          statusCode: status_code,
          invoiceHistoryData:
            payload.offset > 0
              ? [...(state.invoiceHistoryData || []), ...(result || [])]
              : result,
          invoiceHistoryCount: total_count,
        };
      }
    );

    builder.addCase(
      getOthersInvoiceHistoryDataAction.rejected,
      (state, { error }) => ({
        ...state,
        success: 0,
        error: error?.message ?? null,
        invoiceHistoryFetching: false,
      })
    );
    builder.addCase(sendOverdueEmailApiAction.pending, (state) => ({
      ...state,
      commentsFetching: true,
    }));

    builder.addCase(
      sendOverdueEmailApiAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            commentsFetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          commentsFetching: false,
          statusCode: status_code,
          success: success,
          message: message,
        };
      }
    );

    builder.addCase(sendOverdueEmailApiAction.rejected, (state, { error }) => ({
      ...state,
      commentsFetching: false,
      error: error?.message ?? null,
      success: 0,
    }));

    builder.addCase(addOtherInvoiceAction.pending, (state) => ({
      ...state,
      submittingInvoiceForm: true,
    }));

    builder.addCase(addOtherInvoiceAction.fulfilled, (state, { payload }) => {
      const { status_code, message, success } = payload;

      if (success === 0) {
        return {
          ...state,
          submittingInvoiceForm: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        success,
        submittingInvoiceForm: false,
        statusCode: status_code,
        message,
      };
    });

    builder.addCase(addOtherInvoiceAction.rejected, (state, { error }) => ({
      ...state,
      submittingInvoiceForm: false,
      success: 0,
      error: error?.message ?? null,
    }));

    builder.addCase(getSingleOtherInvoiceEntryAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getSingleOtherInvoiceEntryAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          singleOtherInvoiceDetails: result[0],
          message: message,
        };
      }
    );

    builder.addCase(
      getSingleOtherInvoiceEntryAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(updateOtherInvoiceAction.pending, (state) => ({
      ...state,
      submittingInvoiceForm: true,
    }));

    builder.addCase(
      updateOtherInvoiceAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success } = payload;

        if (success === 0) {
          return {
            ...state,
            submittingInvoiceForm: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          success,
          submittingInvoiceForm: false,
          statusCode: status_code,
          message,
        };
      }
    );

    builder.addCase(updateOtherInvoiceAction.rejected, (state, { error }) => ({
      ...state,
      submittingInvoiceForm: false,
      success: 0,
      error: error?.message ?? null,
    }));

    builder.addCase(getGstrTwoATwoBComparePayloadAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getGstrTwoATwoBComparePayloadAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          singleInvoiceDetails: result,
        };
      }
    );

    builder.addCase(
      getGstrTwoATwoBComparePayloadAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(getUploadedBankStatementAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getUploadedBankStatementAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          uploadedBankStatementList: result.data,
          uploadedBankStatementCount: result.total_count,
          message: message,
        };
      }
    );

    builder.addCase(
      getUploadedBankStatementAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(getAllPaymentLedgersListAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getAllPaymentLedgersListAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }
        let finalData: PaymentLedgerType[] = [];
        result &&
          result?.length > 0 &&
          result?.map((tempData: any) => {
            if (tempData?.to_show) {
              finalData.push({
                ...tempData,
              });
            }
          });

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          allPaymentLedgerList: finalData,
          message: message,
        };
      }
    );

    builder.addCase(
      getAllPaymentLedgersListAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(uploadBankStatementAction.pending, (state) => ({
      ...state,
      uploadedBankStatementFetching: true,
    }));
    builder.addCase(
      uploadBankStatementAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            uploadedBankStatementFetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          uploadedBankStatementFetching: false,
          statusCode: status_code,
          success,
          uploadBankStatementMessage: message,
        };
      }
    );

    builder.addCase(uploadBankStatementAction.rejected, (state, { error }) => ({
      ...state,
      uploadedBankStatementFetching: false,
      success: 0,
      error: error?.message ?? null,
    }));

    builder.addCase(
      getUploadedBankStatementTransactionList.pending,
      (state) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getUploadedBankStatementTransactionList.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
          uploadedBankStatementTransactionList: result?.data || [],
          uploadedBankStatementTransactionCount: result?.total_count || 0,
          uploadedBankStatementSummary: result?.summary,
        };
      }
    );

    builder.addCase(
      getUploadedBankStatementTransactionList.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(
      getUploadedBankStatementTransactionCommentList.pending,
      (state) => ({
        ...state,
        transactionCommentFetching: true,
      })
    );

    builder.addCase(
      getUploadedBankStatementTransactionCommentList.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            transactionCommentFetching: false,
            statusCode: status_code,
            transactionCommentSuccess: success,
            error: message,
          };
        }

        return {
          ...state,
          transactionCommentFetching: false,
          statusCode: status_code,
          transactionCommentSuccess: success,
          message,
          bankStatementTransactionCommentList: result.data,
        };
      }
    );

    builder.addCase(
      getUploadedBankStatementTransactionCommentList.rejected,
      (state, { error }) => ({
        ...state,
        transactionCommentFetching: false,
        transactionCommentSuccess: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(postTransactionCommentList.pending, (state) => ({
      ...state,
      postCommentFetching: true,
    }));

    builder.addCase(
      postTransactionCommentList.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success } = payload;

        state.postCommentFetching = false;
        state.statusCode = status_code;
        state.postTransactionCommentSuccess = success;
        state.message = message;
      }
    );

    builder.addCase(postTransactionCommentList.rejected, (state, { error }) => {
      state.postCommentFetching = false;
      state.postTransactionCommentSuccess = 0;
      state.error = error?.message ?? null;
    });

    builder.addCase(allocateBankTransactionToOtherInvoice.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      allocateBankTransactionToOtherInvoice.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      allocateBankTransactionToOtherInvoice.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(postMultipleBankTransactionAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      postMultipleBankTransactionAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      postMultipleBankTransactionAction.rejected,
      (state, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      bankStatementTransactionFileUploadAction.pending,
      (state) => ({
        ...state,
        fileUploadFetching: true,
      })
    );

    builder.addCase(
      bankStatementTransactionFileUploadAction.fulfilled,
      (state, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            fileUploadFetching: false,
            statusCode: status_code,
            error: message,
            success,
          };
        }

        return {
          ...state,
          fileUploadFetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      bankStatementTransactionFileUploadAction.rejected,
      (state, { error }) => ({
        ...state,
        fileUploadFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    // overdue invoice email reminder slice
    builder.addCase(
      getOverdueInvoiceReminderEmailTimelineAction.pending,
      (state) => ({
        ...state,
        emailTimelineFetching: true,
      })
    );

    builder.addCase(
      getOverdueInvoiceReminderEmailTimelineAction.fulfilled,
      (state, { payload }) => {
        const { status_code, message, success, result } = payload;

        if (success === 0) {
          return {
            ...state,
            emailTimelineFetching: false,
            statusCode: status_code,
            success,
            error: message,
          };
        }

        return {
          ...state,
          emailTimelineFetching: false,
          statusCode: status_code,
          success,
          message,
          overdueInvoiceEmailTimelineData: result,
        };
      }
    );

    builder.addCase(
      getOverdueInvoiceReminderEmailTimelineAction.rejected,
      (state, { error }) => ({
        ...state,
        emailTimelineFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(lockUnlockInvoiceAction.pending, (state) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(lockUnlockInvoiceAction.fulfilled, (state, { payload }) => {
      const { status_code, success, message } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          error: message,
          statusCode: status_code,
          success,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        success,
        message,
      };
    });

    builder.addCase(lockUnlockInvoiceAction.rejected, (state, { error }) => ({
      ...state,
      fetching: false,
      error: error?.message ?? null,
      success: 0,
    }));
  },
});

export const {
  setClientCodeInInvoicingAdvance,
  setCompanyNameInInvoicingAdvance,
  resetSearchStringInInvoicingAdvance,
  setSearchStringInInvoicingAdvance,
  togglePaymentStatusFilterInVoicingAdvance,
  setAllPaymentStatusInInvoicingAdvance,
  resetFilterInvoicingAdvance,
  setAllDrawerFilters,
  resetAllDrawerFilters,
  setSortingValue,
  setOverdueReminderToggleValue,
  setActiveAddInvoiceType,
  resetSingleInvoiceDetail,
  resetStatusInfo,
  resetEWayBillError,
  resetPinCodeDistanceCountData,
  resetPinCodeError,
  resetEWayBillReviewData,
  resetEWayBillUpdateError,
  resetInvoicingAdvanceSlice,
  toggleRefetchCustomerSupplierDetails,
  triggerRefetchInvoiceList,
  resetRefetchInvoiceList,
  resetBulkUploadData,
  resetSingleOtherInvoiceDetail,
  triggerRefetchOtherInvoiceList,
  resetRefetchOtherInvoiceList,
  resetUploadBankStatementMessage,
  resetInvoiceDownloadDetails,
  resetUploadedBankStatementTransactionList,
  resetTransactionCommentSuccess,
  resetTransactionCommentList,
  resetBankStatementUploadFetching,
  resetEmailTimelineFetching,
  resetOverdueInvoiceEmailTimelineData,
  resetLockUnlockResponseFetching,
  setBankTransactionFilters,
  resetBankTransactionFilters,
} = invoicingAdvanceSlice.actions;
